import { useEffect, useRef, useState } from 'react'

/**
 * Sets the min-height of the table responsive area to the maximum
 * observed height while loading so skeleton row count changes do not
 * collapse layout (e.g. Cockpit).
 */
const useStabilizedTableResponsiveMinHeight = ({
  enabled,
  isLoading,
  objectCount
}: {
  enabled: boolean
  isLoading: boolean
  objectCount: number
}) => {
  const tableResponsiveRef = useRef<HTMLDivElement>(null)
  const [tableAreaMinHeight, setTableAreaMinHeight] = useState(0)
  const wasLoadingRef = useRef(false)

  useEffect(() => {
    if (!enabled) {
      setTableAreaMinHeight(0)
      wasLoadingRef.current = false
      return
    }

    const el = tableResponsiveRef.current
    if (!el) return

    const h = el.getBoundingClientRect().height
    if (h <= 0) return

    if (isLoading) {
      wasLoadingRef.current = true
      setTableAreaMinHeight((prev) => Math.max(prev, h))
      return
    }

    if (wasLoadingRef.current) {
      setTableAreaMinHeight(0)
      wasLoadingRef.current = false
    }
  }, [enabled, isLoading, objectCount])

  const tableResponsiveMinHeightStyle =
    enabled && tableAreaMinHeight
      ? { minHeight: tableAreaMinHeight }
      : undefined

  return { tableResponsiveRef, tableResponsiveMinHeightStyle }
}

export default useStabilizedTableResponsiveMinHeight
